import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { RootState } from "../../store";
import { Wallet, Eye, EyeOff, Loader } from "lucide-react";
import { AuthService } from "../../services/auth/authService";
import { setToken } from "../../store/slices/authSlice";
import { getErrorMessage } from "../../utils/errorHelpers";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

export const Register: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [entrepriseNom, setEntrepriseNom] = useState("");
  const [siret, setSiret] = useState("");
  const [telephone, setTelephone] = useState("");
  const [role, setRole] = useState("fournisseur");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, error } = useSelector((state: RootState) => state.auth);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t('auth.register.passwordMismatch'));
      return;
    }

    try {
      const userData = {
        email,
        password,
        role,
        entreprise_nom: entrepriseNom,
        siret,
        telephone,password_confirm:confirmPassword
      };
      
      const response = await AuthService.register(userData);
      
      // Auto-login
      dispatch(setToken(response.data.access));
      
      toast.success(t('auth.register.success'));
      navigate("/login");
      
    } catch (err: any) {
      console.error("Erreur lors de l'inscription:", err);
      const message = getErrorMessage(err);
      toast.error(message);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div>
            <div className="flex items-center">
              <Wallet className="h-8 w-8 text-primary-600" />
              <span className="ml-2 text-2xl font-bold text-gray-900">
                Afflux
              </span>
            </div>
            <h2 className="mt-8 text-3xl font-bold tracking-tight text-gray-900">
              {t('auth.register.title')}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {t('auth.register.alreadyHaveAccount')}{" "}
              <Link
                to="/login"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                {t('auth.register.loginLink')}
              </Link>
            </p>
          </div>

          <div className="mt-8">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="rounded-md bg-error-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-error-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-error-800">
                        {t('auth.register.error')}
                      </h3>
                      <div className="mt-1 text-sm text-error-700">{error}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Type de compte */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.register.roleLabel')}
                </label>
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setRole("fournisseur")}
                    className={`flex-1 py-2 px-4 border rounded-md text-sm font-medium ${
                      role === "fournisseur"
                        ? "border-primary-500 bg-primary-50 text-primary-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {t('auth.register.roles.fournisseur')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRole("client")}
                    className={`flex-1 py-2 px-4 border rounded-md text-sm font-medium ${
                      role === "client"
                        ? "border-primary-500 bg-primary-50 text-primary-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {t('auth.register.roles.client')}
                  </button>
                </div>
              </div>

              {/* Informations entreprise */}
              <div>
                <label
                  htmlFor="entrepriseNom"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('auth.register.companyName')}
                </label>
                <input
                  id="entrepriseNom"
                  type="text"
                  required
                  value={entrepriseNom}
                  onChange={(e) => setEntrepriseNom(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  placeholder={t('auth.register.companyPlaceholder')}
                />
              </div>

              <div>
                <label
                  htmlFor="siret"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('auth.register.siret')}
                </label>
                <input
                  id="siret"
                  type="text"
                  value={siret}
                  onChange={(e) => setSiret(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  placeholder="123 456 789 00012"
                />
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('auth.register.emailLabel')}
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  placeholder={t('auth.login.emailPlaceholder')}
                />
              </div>

              {/* Téléphone */}
              <div>
                <label
                  htmlFor="telephone"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('auth.register.phone')}
                </label>
                <input
                  id="telephone"
                  type="tel"
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  placeholder={t('auth.register.phonePlaceholder')}
                />
              </div>

              {/* Mot de passe */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('auth.register.password')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm pr-10"
                    placeholder={t('auth.register.passwordHint')}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirmation mot de passe */}
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('auth.register.confirmPassword')}
                </label>
                <input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  placeholder={t('auth.register.confirmPasswordPlaceholder')}
                />
              </div>

              {/* Conditions */}
              <div className="flex items-center">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  required
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label
                  htmlFor="terms"
                  className="ml-2 block text-sm text-gray-900"
                >
                  {t('auth.register.termsPrefix')}{" "}
                  <a
                    href="https://www.affluxcorp.com/mentions-legales.html#cgu"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-500"
                  >
                    {t('auth.register.termsLink')}
                  </a>{" "}
                  {t('auth.register.termsAnd')}{" "}
                  <a
                    href="https://www.affluxcorp.com/mentions-legales.html#confidentialite"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-500"
                  >
                    {t('auth.register.privacyLink')}
                  </a>
                </label>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex w-full justify-center rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <Loader className="h-5 w-5 animate-spin" />
                  ) : (
                    t('auth.register.submit', { role: role === "client" ? t('auth.register.roles.client') : t('auth.register.roles.fournisseur') })
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative flex-1">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-primary-800">
          <div className="flex items-center justify-center h-full p-12">
            <div className="text-center text-white">
              <Wallet className="h-24 w-24 mx-auto mb-8 opacity-80" />
              <h3 className="text-3xl font-bold mb-4">
                {t('auth.login.illustrationTitle')}
              </h3>
              <p className="text-xl opacity-90">
                Optimisez votre trésorerie et renforcez vos relations
                commerciales
              </p>
              <div className="mt-12 space-y-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <p className="ml-4 text-lg">
                    Financement rapide de vos factures
                  </p>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <p className="ml-4 text-lg">
                    Relations commerciales sécurisées
                  </p>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <p className="ml-4 text-lg">Plateforme 100% digitale</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
